/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { apDefaultsFromInventory } from './apModels'
import { defaultMountHeight, normalizeMountType } from './apMounts'
import { assignOptimalChannels } from './rfChannels'
import { computeFloorMetrics, optimizationGrid, type FloorPlanMetrics } from './rfFloorMetrics'
import {
  applyChannelPlan,
  findGapApPosition,
  hasAutoFix,
  MIN_GAP_AP_SEPARATION_M,
  weakZoneCentroid,
} from './rfRecommendationFixes'
import { reconcileApAfterMove } from './rfOptimizerGeometry'
import { computeRfRecommendations } from './rfRecommendations'
import { computeHeatmapGrid } from './rfSimulation'
import { RF_GRID_COLS, RF_GRID_ROWS } from './rfGrid'
import { distanceToNearestWall } from './hitTest'
import {
  coverageOk,
  MAX_WEAK_RISE_PCT,
  planAcceptable,
  planImproves,
} from './rfOptimizerGuards'
import type { ApMountType, FloorGeometry, FloorRecommendation, PlacedAp } from './types'

export type SimulatedNewAp = {
  apName: string
  x: number
  y: number
  txPowerDbm: number
  channel: number
  mount: ApMountType
  mountHeightM: number
  mountOrientationDeg?: number
  band?: string
}

export type GapApInventoryDevice = { id: string; name: string; model?: string }

function gapMountCandidates(
  gapPos: { x: number; y: number },
  geometry: FloorGeometry,
  inventoryMount: ApMountType,
): ApMountType[] {
  const mounts: ApMountType[] = [inventoryMount]
  if (geometry.walls.length === 0) return mounts

  const wallDist = distanceToNearestWall(gapPos.x, gapPos.y, geometry.walls)
  const hasObstacles = geometry.obstacles.length > 0

  // Consider wall mount whenever the gap is near any partition (including perimeter walls).
  if (inventoryMount !== 'wall' && wallDist <= 15) mounts.push('wall')
  // Pillars / racks — wall mount often clears metal shadow zones along the perimeter.
  if (hasObstacles && wallDist <= 22 && !mounts.includes('wall')) mounts.push('wall')
  return mounts
}

function gapPlacementForMount(
  apName: string,
  gapPos: { x: number; y: number },
  mount: ApMountType,
  defaults: ReturnType<typeof apDefaultsFromInventory>,
  weakCentroid: { x: number; y: number } | null,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): SimulatedNewAp {
  const draft: PlacedAp = {
    id: 'gap-draft',
    ap: apName,
    x: gapPos.x,
    y: gapPos.y,
    txPowerDbm: defaults.txPowerDbm,
    mount,
    mountHeightM: mount === defaults.mount ? defaults.mountHeightM : defaultMountHeight(mount),
    band: defaults.band,
  }
  const reconciled = reconcileApAfterMove(
    draft,
    gapPos.x,
    gapPos.y,
    geometry,
    floorWidthM,
    floorHeightM,
    weakCentroid,
  )
  return {
    apName,
    x: reconciled.x,
    y: reconciled.y,
    txPowerDbm: reconciled.txPowerDbm ?? defaults.txPowerDbm,
    channel: 0,
    mount: normalizeMountType(reconciled.mount ?? mount),
    mountHeightM: reconciled.mountHeightM ?? defaultMountHeight(mount),
    mountOrientationDeg: reconciled.mountOrientationDeg,
    band: defaults.band,
  }
}

/** Mount-aware gap placement — tries wall mount near partitions when pillars or wall proximity favor it. */
export function buildGapApPlacement(
  apName: string,
  inventoryAps: GapApInventoryDevice[] | undefined,
  gapPos: { x: number; y: number },
  placedAps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): SimulatedNewAp {
  const inv = inventoryAps?.find(a => a.name === apName)
  const defaults = apDefaultsFromInventory(inv, geometry.bandGHz)
  const grid = computeHeatmapGrid(placedAps, geometry, floorWidthM, floorHeightM, RF_GRID_COLS, RF_GRID_ROWS)
  const weakCentroid = weakZoneCentroid(grid, geometry, floorWidthM, floorHeightM)
  const mounts = gapMountCandidates(gapPos, geometry, defaults.mount)

  let best: SimulatedNewAp | null = null
  let bestWeak = Infinity

  for (const mount of mounts) {
    const placement = gapPlacementForMount(
      apName,
      gapPos,
      mount,
      defaults,
      weakCentroid,
      geometry,
      floorWidthM,
      floorHeightM,
    )
    const trial = simulatedNewApToPlacedAp(placement, 'gap-trial')
    const metrics = computeFloorMetrics([...placedAps, trial], geometry, floorWidthM, floorHeightM, grid)
    if (metrics.weakPct < bestWeak) {
      bestWeak = metrics.weakPct
      best = placement
    }
  }

  return (
    best ??
    gapPlacementForMount(
      apName,
      gapPos,
      defaults.mount,
      defaults,
      weakCentroid,
      geometry,
      floorWidthM,
      floorHeightM,
    )
  )
}

export function simulatedNewApToPlacedAp(n: SimulatedNewAp, id: string): PlacedAp {
  return {
    id,
    ap: n.apName,
    x: n.x,
    y: n.y,
    txPowerDbm: n.txPowerDbm,
    channel: n.channel,
    mount: n.mount,
    mountHeightM: n.mountHeightM,
    mountOrientationDeg: n.mountOrientationDeg,
    band: n.band,
  }
}

export function gapApPlaceRequestBody(
  placement: SimulatedNewAp,
  deviceId?: string,
): Record<string, unknown> {
  return {
    apName: placement.apName,
    x: placement.x,
    y: placement.y,
    txPowerDbm: placement.txPowerDbm,
    deviceId,
    band: placement.band,
    mount: placement.mount,
    mountHeightM: placement.mountHeightM,
    ...(placement.mount === 'wall'
      ? { mountOrientationDeg: placement.mountOrientationDeg ?? 0 }
      : {}),
  }
}

export type RecommendationApplyResult = {
  aps: PlacedAp[]
  newAps: SimulatedNewAp[]
  channels: Record<string, number>
  addressed: string[]
  skipped: string[]
  remaining: FloorRecommendation[]
}

function distanceM(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const dx = ((x2 - x1) / 100) * floorWidthM
  const dy = ((y2 - y1) / 100) * floorHeightM
  return Math.hypot(dx, dy)
}

function withChannels(aps: PlacedAp[], channels: Record<string, number>): PlacedAp[] {
  return aps.map(ap => ({ ...ap, channel: channels[ap.id] ?? ap.channel }))
}

function nearestApDistanceM(
  x: number,
  y: number,
  aps: PlacedAp[],
  floorWidthM: number,
  floorHeightM: number,
): number {
  if (aps.length === 0) return Infinity
  return Math.min(...aps.map(ap => distanceM(ap.x, ap.y, x, y, floorWidthM, floorHeightM)))
}

export type ApplyRecommendationFixOptions = {
  inventoryAps?: GapApInventoryDevice[]
  existingNewAps?: SimulatedNewAp[]
  /** When true, apply TX/channel fixes but skip AP repositioning (interference-only optimize). */
  skipMove?: boolean
}

/** Apply one recommendation fix to an in-memory AP list (no API / geometry edits). */
export function applyRecommendationFixInMemory(
  rec: FloorRecommendation,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  options: ApplyRecommendationFixOptions = {},
): { aps: PlacedAp[]; newAp?: SimulatedNewAp; applied: boolean } {
  const { inventoryAps, existingNewAps = [], skipMove = false } = options
  const fix = rec.fix
  if (!fix || !hasAutoFix(rec)) return { aps, applied: false }
  if (rec.action === 'draw_walls' || rec.action === 'draw_obstacles') {
    return { aps, applied: false }
  }

  let next = aps.map(a => ({ ...a }))
  let applied = false
  let newAp: SimulatedNewAp | undefined

  if (fix.moveApId && fix.moveTo && !skipMove) {
    const idx = next.findIndex(a => a.id === fix.moveApId)
    if (idx >= 0) {
      const reconciled = reconcileApAfterMove(
        next[idx],
        fix.moveTo.x,
        fix.moveTo.y,
        geometry,
        floorWidthM,
        floorHeightM,
        fix.moveTo,
      )
      if (
        Math.hypot(next[idx].x - reconciled.x, next[idx].y - reconciled.y) > 0.5
        || Math.abs((next[idx].mountOrientationDeg ?? 0) - (reconciled.mountOrientationDeg ?? 0)) > 5
      ) {
        next[idx] = reconciled
        applied = true
      }
    }
  }

  if (fix.txByApId && fix.apIds?.length) {
    for (const id of fix.apIds) {
      const tx = fix.txByApId[id]
      const idx = next.findIndex(a => a.id === id)
      if (idx < 0 || tx == null || tx >= (next[idx].txPowerDbm ?? 15)) continue
      next[idx] = { ...next[idx], txPowerDbm: tx }
      applied = true
    }
  }

  if (fix.newApAt && inventoryAps?.length) {
    const placedNames = new Set([
      ...next.map(a => a.ap),
      ...existingNewAps.map(n => n.apName),
    ])
    const apName = inventoryAps.find(a => !placedNames.has(a.name))?.name
    if (apName) {
      const grid = optimizationGrid(next, geometry, floorWidthM, floorHeightM)
      const gapPos = findGapApPosition(next, grid, floorWidthM, floorHeightM, geometry) ?? fix.newApAt
      if (nearestApDistanceM(gapPos.x, gapPos.y, next, floorWidthM, floorHeightM) >= MIN_GAP_AP_SEPARATION_M) {
        const placement = buildGapApPlacement(
          apName,
          inventoryAps,
          gapPos,
          next,
          geometry,
          floorWidthM,
          floorHeightM,
        )
        const virtualId = `rec-trial-${apName}`
        const trial = [...next, simulatedNewApToPlacedAp(placement, virtualId)]
        const ch = assignOptimalChannels(trial, geometry.bandGHz, floorWidthM, floorHeightM)
        newAp = {
          ...placement,
          channel: ch[virtualId] ?? 36,
        }
        applied = true
      }
    }
  }

  return { aps: next, newAp, applied }
}

export type ApplyOptimizerRecommendationsOptions = {
  inventoryAps?: GapApInventoryDevice[]
  maxNewAps?: number
  metricsBefore: FloorPlanMetrics
  interferenceOnly?: boolean
}

/** Apply auto-fixable RF recommendations with the same guards as floor optimization. */
export function applyOptimizerRecommendations(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  options: ApplyOptimizerRecommendationsOptions,
): RecommendationApplyResult {
  const { inventoryAps, maxNewAps = 3, metricsBefore, interferenceOnly = false } = options
  const recs = computeRfRecommendations(aps, geometry, floorWidthM, floorHeightM)
  const autoFixable = recs.filter(r => {
    if (!hasAutoFix(r) || r.action === 'draw_walls' || r.action === 'draw_obstacles') return false
    if (interferenceOnly) {
      if (r.action === 'relocate_ap') return false
      if (r.fix?.moveApId && !r.fix?.txByApId) return false
      if (r.action === 'add_ap' && metricsBefore.weakPct <= 18) return false
    }
    return true
  })

  let working = aps.map(a => ({ ...a }))
  const newAps: SimulatedNewAp[] = []
  const addressed: string[] = []
  const skipped: string[] = []
  let channels: Record<string, number> = {}

  for (const rec of autoFixable) {
    if (rec.action === 'add_ap' && newAps.length >= maxNewAps) {
      skipped.push(rec.title)
      continue
    }

    const skipMove =
      interferenceOnly
      && (
        rec.action === 'relocate_ap'
        || (!!rec.fix?.moveApId && !rec.fix?.txByApId)
      )
    const trial = applyRecommendationFixInMemory(rec, working, geometry, floorWidthM, floorHeightM, {
      inventoryAps,
      existingNewAps: newAps,
      skipMove,
    })
    if (!trial.applied) {
      skipped.push(rec.title)
      continue
    }

    let trialAps = trial.aps
    const trialNew = trial.newAp ? [...newAps, trial.newAp] : [...newAps]
    const planned: PlacedAp[] = trialNew.map((n, i) => simulatedNewApToPlacedAp(n, `rec-plan-${i}`))
    const chPlan = applyChannelPlan([...trialAps, ...planned], geometry.bandGHz, floorWidthM, floorHeightM)
    trialAps = chPlan.aps.filter(a => !a.id.startsWith('rec-plan-'))
    const metrics = computeFloorMetrics([...trialAps, ...planned], geometry, floorWidthM, floorHeightM)

    const currentPlanned = newAps.map((n, i) => simulatedNewApToPlacedAp(n, `cur-plan-${i}`))
    const beforeStep = computeFloorMetrics([...working, ...currentPlanned], geometry, floorWidthM, floorHeightM)

    const acceptable =
      planAcceptable(metrics, metricsBefore)
      || (
        interferenceOnly
        && coverageOk(metrics, metricsBefore)
        && metrics.highInterfPct <= beforeStep.highInterfPct - 1
      )
      || (
        planImproves(metrics, metricsBefore)
        && metrics.weakPct <= beforeStep.weakPct + MAX_WEAK_RISE_PCT
        && metrics.highInterfPct <= beforeStep.highInterfPct + 2
      )

    if (!acceptable) {
      skipped.push(rec.title)
      continue
    }

    working = trialAps
    if (trial.newAp) newAps.push(trial.newAp)
    channels = chPlan.channels
    addressed.push(rec.title)
  }

  if (working.length >= 2 && Object.keys(channels).length === 0) {
    const chPlan = applyChannelPlan(working, geometry.bandGHz, floorWidthM, floorHeightM)
    working = chPlan.aps
    channels = chPlan.channels
  } else if (working.length >= 2) {
    working = withChannels(working, channels)
  }

  const remaining = computeRfRecommendations(
    [
      ...working,
      ...newAps.map((n, i) => simulatedNewApToPlacedAp(n, `rem-${i}`)),
    ],
    geometry,
    floorWidthM,
    floorHeightM,
  )

  return { aps: working, newAps, channels, addressed, skipped, remaining }
}
